<template>
    <!-- Rendered twice by a page, once per layout header slot: the buttons as the subtitle, the crumbs as the title. -->
    <div
        v-if="props.part === 'subtitle' && (computedHomeHref || computedBackButtonHref)"
        class="flex flex-row items-center gap-2"
    >
        <vx-button
            v-if="computedHomeHref"
            color="muted-dark"
            size="sm"
            :href="computedHomeHref"
            data-test="home-btn"
            aria-label="Franchise Hub"
        >
            <font-awesome-icon widthAuto :icon="faHome" />
        </vx-button>
        <vx-button
            v-if="computedBackButtonHref"
            color="muted-dark"
            size="sm"
            :href="computedBackButtonHref"
            data-test="back-btn"
        >
            {{ backButtonText }}
            <font-awesome-icon widthAuto :icon="faSquareArrowUpLeft" />
        </vx-button>
    </div>

    <span v-else-if="props.part === 'title'" class="inline-flex flex-row items-center flex-wrap gap-4">
        <template v-for="(item, index) in breadcrumbItems" :key="index">
            <font-awesome-icon v-if="item.icon" widthAuto class="text-slate-500" :icon="item.icon" />
            <span v-if="index < breadcrumbItems.length - 1" class="text-slate-500">
                {{ item.label }}
            </span>
            <span v-else class="text-slate-700" data-test="page-title">
                {{ item.label }}
            </span>
            <span v-if="index < breadcrumbItems.length - 1" class="text-slate-800 text-base"> / </span>
        </template>
    </span>
</template>

<script setup>
import { computed } from 'vue';
import { faHome, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton } from '@voxie/frontend-components';
import { stringifyQueryString } from '~/utils/string';
import { analyticsCategoriesValues, getCategoryByValue } from '~/constants/categories';
import { getMetricByValue, metricsWithGoals } from '~/utils/analytics';

const props = defineProps({
    part: {
        type: String,
        required: true,
        validator: (part) => ['subtitle', 'title'].includes(part),
    },
    homeHref: {
        type: String,
        default: '/analytics/franchise-hub',
    },
    backButtonHref: {
        type: String,
        required: false,
    },
    backButtonText: {
        type: String,
        default: 'Back',
    },
    pageTitle: {
        type: String,
        required: false,
    },
    fetchParams: {
        type: Object,
        required: false,
    },
    category: {
        type: String,
        required: false,
        validator: (category) => analyticsCategoriesValues.includes(category),
    },
    metric: {
        type: String,
        required: false,
        validator: (metric) => metricsWithGoals.includes(metric),
    },
});

const activeCategory = computed(() => getCategoryByValue(props.category));
const activeMetric = computed(() => getMetricByValue(props.metric));

const computedHomeHref = computed(() => {
    if (!props.homeHref) {
        return null;
    }

    if (!props.fetchParams) {
        return props.homeHref;
    }

    return `${props.homeHref}?${stringifyQueryString(props.fetchParams)}`;
});

const computedBackButtonHref = computed(() => {
    if (!props.backButtonHref) {
        return null;
    }

    if (!props.fetchParams) {
        return props.backButtonHref;
    }

    return `${props.backButtonHref}?${stringifyQueryString(props.fetchParams)}`;
});

const breadcrumbItems = computed(() => {
    const items = [];

    if (activeCategory.value) {
        items.push({
            icon: activeCategory.value?.icon,
            label: activeCategory.value?.label,
        });

        if (activeMetric.value) {
            items.push({ label: activeMetric.value?.label });
        }
    }

    if (props.pageTitle) {
        items.push({ label: props.pageTitle });
    }

    return items;
});
</script>
